// Status words (APS-412, admin-d.md "Status"): the console's glyph and a lowercase word in the
// state's AA tone, never a pill: `✓ ativa`, `! suspensa`, `× falhou`, `○ agendada até 21 de
// out.`, `– cancelada`. One place maps each admin state (organization, erasure, NFS-e, webhook
// delivery) to its tone, so every screen says a state the same way.
import { Status, type StatusKey } from '@apsio/ui'
import type { ErasureStatus, NfseStatus, OrgDetail } from '../../api/queries'
import { type MessageId, useI18n } from '../../i18n'

type Tone = { s: StatusKey; glyph: string }
const OK: Tone = { s: 'ok', glyph: '✓' }
const WARN: Tone = { s: 'miss', glyph: '!' }
const FAIL: Tone = { s: 'fire', glyph: '×' }
const WAIT: Tone = { s: 'on', glyph: '○' }
const BUSY: Tone = { s: 'on', glyph: '◐' }
const OFF: Tone = { s: 'mute', glyph: '–' }

type OrgStatus = OrgDetail['status']
const ORG: Record<OrgStatus, [Tone, MessageId]> = {
  active: [OK, 'status.active'],
  suspended: [WARN, 'status.suspended'],
  payment_failed: [FAIL, 'status.paymentFailed'],
}

const ERASURE: Record<ErasureStatus, [Tone, MessageId]> = {
  scheduled: [WAIT, 'erasure.status.scheduled'],
  running: [BUSY, 'erasure.status.running'],
  done: [OK, 'erasure.status.done'],
  canceled: [OFF, 'erasure.status.canceled'],
  failed: [FAIL, 'erasure.status.failed'],
}

const NFSE: Record<NfseStatus, [Tone, MessageId]> = {
  manual: [WARN, 'nfse.status.manual'],
  pending: [WAIT, 'nfse.status.pending'],
  sending: [BUSY, 'nfse.status.sending'],
  retrying: [BUSY, 'nfse.status.retrying'],
  rejected: [FAIL, 'nfse.status.rejected'],
  failed: [FAIL, 'nfse.status.failed'],
  held: [WARN, 'nfse.status.held'],
  issued: [OK, 'nfse.status.issued'],
  issued_by_hand: [OK, 'nfse.status.issuedByHand'],
  cancel_pending: [WAIT, 'nfse.status.cancelPending'],
  cancelling: [BUSY, 'nfse.status.cancelling'],
  cancel_manual: [WARN, 'nfse.status.cancelManual'],
  cancel_failed: [FAIL, 'nfse.status.cancelFailed'],
  cancelled: [OFF, 'nfse.status.cancelled'],
}

/**
 * A webhook delivery's status is the provider's own word (Hookdeck): said in the page's language
 * by what it means, the raw word kept as the title for support.
 */
function webhook(status: string): [Tone, MessageId] {
  const s = status.toLowerCase()
  if (/success|deliver|^ok$/.test(s)) return [OK, 'kit.webhook.delivered']
  if (/fail|error/.test(s)) return [FAIL, 'kit.webhook.failed']
  if (/cancel|ignor/.test(s)) return [OFF, 'kit.webhook.cancelled']
  if (/hold|pause/.test(s)) return [WARN, 'kit.webhook.held']
  return [WAIT, 'kit.webhook.queued']
}

export type StatusSubject =
  | { kind: 'org'; status: OrgStatus }
  | { kind: 'erasure'; status: ErasureStatus }
  | { kind: 'nfse'; status: NfseStatus }
  | { kind: 'webhook'; status: string }

/** `until`: a date after the word (`○ agendada até 21 de out.`), already formatted. */
export function StatusWord({ until, ...subject }: StatusSubject & { until?: string | undefined }) {
  const { t, locale } = useI18n()
  const entry =
    subject.kind === 'org'
      ? ORG[subject.status]
      : subject.kind === 'erasure'
        ? ERASURE[subject.status]
        : subject.kind === 'nfse'
          ? NFSE[subject.status]
          : webhook(subject.status)
  const [tone, word] = entry
  const text = t(word).toLocaleLowerCase(locale)
  const status = (
    <Status
      s={tone.s}
      glyph={tone.glyph}
      label={until ? t('kit.status.until', { status: text, date: until }) : text}
    />
  )
  return subject.kind === 'webhook' ? (
    <span title={subject.status} translate="no">
      {status}
    </span>
  ) : (
    status
  )
}
